// Snapshot Editor - Full-screen annotation editor for camera snapshots
// Uses Konva for canvas-based drawing tools (arrows, shapes, freehand, text)
// Supports sharing via built-in chat and email

import { useState, useEffect, useRef, useCallback } from 'react';
import { Stage, Layer, Line, Rect, Circle, Arrow, Text as KonvaText, Image as KonvaImage, Transformer } from 'react-konva';
import type { KonvaEventObject } from 'konva/lib/Node';
import type { Stage as StageType } from 'konva/lib/Stage';
import Konva from 'konva';
import {
  MousePointer2,
  MoveRight,
  Square,
  CircleIcon,
  Pencil,
  Type,
  Eraser,
  Undo2,
  Redo2,
  Download,
  MessageSquare,
  Mail,
  X,
  Search,
  Check,
  Users,
  User,
  Send,
  Loader2,
  Camera,
  AlertTriangle,
} from 'lucide-react';
import { usersApi, userGroupsApi, chatApi, devicesApi } from '../../services/api';
import { useChatStore } from '../../stores/chat.store';
import { useAuthStore } from '../../stores/auth.store';
import { useUIStore } from '../../stores/ui.store';

import { useTranslation } from 'react-i18next';
// ============================================
// TYPES
// ============================================

type AnnotationTool = 'select' | 'arrow' | 'rectangle' | 'circle' | 'freehand' | 'text' | 'eraser';

interface Annotation {
  id: string;
  type: 'arrow' | 'rectangle' | 'circle' | 'freehand' | 'text';
  points?: number[];
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  radiusX?: number;
  radiusY?: number;
  text?: string;
  color: string;
  strokeWidth: number;
  fontSize?: number;
}

interface SnapshotEditorProps {
  blob: Blob;
  cameraName: string;
  onClose: () => void;
  /**
   * Optional camera identity. When present, the "Create incident"
   * toolbar button looks up the camera's device record to prefill the
   * incident's lat/lng. Both forms are accepted because NX cameras key
   * off `{instanceId}-{cameraUUID}` while channel-based drivers key
   * off the channel id — UnifiedCameraPlayer passes whichever it has.
   */
  cameraDeviceId?: string;
  cameraInstanceId?: string;
}

const COLORS = [
  { value: '#ef4444', label: 'Red' },
  { value: '#eab308', label: 'Yellow' },
  { value: '#22c55e', label: 'Green' },
  { value: '#3b82f6', label: 'Blue' },
  { value: '#ffffff', label: 'White' },
  { value: '#000000', label: 'Black' },
];

const THICKNESSES = [
  { value: 2, label: 'Thin' },
  { value: 4, label: 'Medium' },
  { value: 6, label: 'Thick' },
];

const TOOLS: { tool: AnnotationTool; icon: typeof MousePointer2; label: string }[] = [
  { tool: 'select', icon: MousePointer2, label: 'Select' },
  { tool: 'arrow', icon: MoveRight, label: 'Arrow' },
  { tool: 'rectangle', icon: Square, label: 'Rectangle' },
  { tool: 'circle', icon: CircleIcon, label: 'Circle' },
  { tool: 'freehand', icon: Pencil, label: 'Freehand' },
  { tool: 'text', icon: Type, label: 'Text' },
  { tool: 'eraser', icon: Eraser, label: 'Eraser' },
];

let annotationIdCounter = 0;
const nextId = () => `ann-${Date.now()}-${++annotationIdCounter}`;

// ============================================
// MAIN COMPONENT
// ============================================

export default function SnapshotEditor({ blob, cameraName, onClose, cameraDeviceId, cameraInstanceId: _cameraInstanceId }: SnapshotEditorProps) {
  const { t } = useTranslation('cameras');
  // Image
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });

  // Floating window drag
  const windowRef = useRef<HTMLDivElement>(null);
  const [windowSize, setWindowSize] = useState(() => {
    const w = Math.round(Math.min(window.innerWidth - 120, 1200) * 0.85);
    const h = Math.round(Math.min(window.innerHeight - 80, 800) * 0.85);
    return { width: w, height: h };
  });
  const [windowPos, setWindowPos] = useState(() => ({
    x: Math.round((window.innerWidth - Math.min(window.innerWidth - 120, 1200) * 0.85) / 2),
    y: Math.round((window.innerHeight - Math.min(window.innerHeight - 80, 800) * 0.85) / 2),
  }));
  const [isDraggingWindow, setIsDraggingWindow] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const resizeStart = useRef({ x: 0, y: 0, w: 0, h: 0 });

  // Canvas
  const containerRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<StageType>(null);
  const transformerRef = useRef<Konva.Transformer>(null);
  const [stageSize, setStageSize] = useState({ width: 800, height: 600 });
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  // Tools
  const [activeTool, setActiveTool] = useState<AnnotationTool>('freehand');
  const [activeColor, setActiveColor] = useState('#ef4444');
  const [activeThickness, setActiveThickness] = useState(4);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Drawing state
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentAnnotation, setCurrentAnnotation] = useState<Annotation | null>(null);

  // History (undo/redo)
  const [history, setHistory] = useState<Annotation[][]>([[]]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const annotations = history[historyIndex] || [];

  // Text input overlay
  const [textInput, setTextInput] = useState<{ x: number; y: number; visible: boolean }>({ x: 0, y: 0, visible: false });
  const [textValue, setTextValue] = useState('');
  const textInputRef = useRef<HTMLInputElement>(null);

  // Sharing dialogs
  const [showChatPicker, setShowChatPicker] = useState(false);
  const [showEmailPicker, setShowEmailPicker] = useState(false);

  // ============================================
  // IMAGE LOADING
  // ============================================

  useEffect(() => {
    const img = new window.Image();
    const url = URL.createObjectURL(blob);
    img.onload = () => {
      setImage(img);
      setImageSize({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.src = url;
    return () => URL.revokeObjectURL(url);
  }, [blob]);

  // IOSentra brand watermark, rasterized once from the inline logo SVG (kept in
  // sync with IOSentraLogo.tsx). Because it's a Konva node in the exported stage,
  // it's baked into every download/share of a manual snapshot automatically.
  const [watermark, setWatermark] = useState<HTMLImageElement | null>(null);
  useEffect(() => {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="256" height="256" viewBox="0 0 256 256" fill="none"><defs><linearGradient id="wm" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#00D4C3"/><stop offset="100%" stop-color="#0866FF"/></linearGradient></defs><path d="M194 82 A86 86 0 1 0 194 174" fill="none" stroke="url(#wm)" stroke-width="15" stroke-linecap="butt"/><path d="M31 128 L88 128 L103 82 L122 174 L145 112 L159 128 L211 128" fill="none" stroke="url(#wm)" stroke-width="11" stroke-linecap="round" stroke-linejoin="round"/><circle cx="214" cy="128" r="10" fill="url(#wm)"/></svg>`;
    const wm = new window.Image();
    wm.onload = () => setWatermark(wm);
    wm.src = `data:image/svg+xml;base64,${window.btoa(svg)}`;
  }, []);

  // ============================================
  // STAGE SIZING
  // ============================================

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateSize = () => {
      const rect = container.getBoundingClientRect();
      setStageSize({ width: rect.width, height: rect.height });
    };

    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // Compute scale + offset to fit image in stage
  useEffect(() => {
    if (!imageSize.width || !imageSize.height || !stageSize.width || !stageSize.height) return;

    const scaleX = stageSize.width / imageSize.width;
    const scaleY = stageSize.height / imageSize.height;
    const fitScale = Math.min(scaleX, scaleY);
    setScale(fitScale);

    const offsetX = (stageSize.width - imageSize.width * fitScale) / 2;
    const offsetY = (stageSize.height - imageSize.height * fitScale) / 2;
    setOffset({ x: offsetX, y: offsetY });
  }, [imageSize, stageSize]);

  // ============================================
  // HISTORY HELPERS
  // ============================================

  const pushHistory = useCallback((newAnnotations: Annotation[]) => {
    setHistory((prev) => {
      const trimmed = prev.slice(0, historyIndex + 1);
      return [...trimmed, newAnnotations];
    });
    setHistoryIndex((prev) => prev + 1);
  }, [historyIndex]);

  const undo = () => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setSelectedId(null);
    }
  };

  const redo = () => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setSelectedId(null);
    }
  };

  // ============================================
  // POINTER TO IMAGE COORDS
  // ============================================

  const getImageCoords = (e: KonvaEventObject<MouseEvent | TouchEvent>) => {
    const stage = stageRef.current;
    if (!stage) return { x: 0, y: 0 };
    const pos = stage.getPointerPosition();
    if (!pos) return { x: 0, y: 0 };
    return {
      x: (pos.x - offset.x) / scale,
      y: (pos.y - offset.y) / scale,
    };
  };

  // ============================================
  // DRAWING HANDLERS
  // ============================================

  const handleMouseDown = (e: KonvaEventObject<MouseEvent>) => {
    if (textInput.visible) return;
    const pos = getImageCoords(e);

    if (activeTool === 'eraser') {
      const target = e.target;
      const id = target?.attrs?.id;
      if (id && id.startsWith('ann-')) {
        const newAnns = annotations.filter((a) => a.id !== id);
        pushHistory(newAnns);
      }
      return;
    }

    if (activeTool === 'select') {
      const target = e.target;
      const id = target?.attrs?.id;
      if (id && id.startsWith('ann-')) {
        setSelectedId(id);
        const node = stageRef.current?.findOne(`#${id}`);
        if (node && transformerRef.current) {
          transformerRef.current.nodes([node]);
          transformerRef.current.getLayer()?.batchDraw();
        }
      } else {
        setSelectedId(null);
        if (transformerRef.current) {
          transformerRef.current.nodes([]);
          transformerRef.current.getLayer()?.batchDraw();
        }
      }
      return;
    }

    if (activeTool === 'text') {
      const stage = stageRef.current;
      if (!stage) return;
      const stagePos = stage.getPointerPosition();
      if (!stagePos) return;
      setTextInput({ x: stagePos.x, y: stagePos.y, visible: true });
      setTextValue('');
      setTimeout(() => textInputRef.current?.focus(), 50);
      return;
    }

    setIsDrawing(true);
    const id = nextId();

    if (activeTool === 'freehand') {
      setCurrentAnnotation({
        id,
        type: 'freehand',
        points: [pos.x, pos.y],
        color: activeColor,
        strokeWidth: activeThickness,
      });
    } else if (activeTool === 'arrow') {
      setCurrentAnnotation({
        id,
        type: 'arrow',
        points: [pos.x, pos.y, pos.x, pos.y],
        color: activeColor,
        strokeWidth: activeThickness,
      });
    } else if (activeTool === 'rectangle') {
      setCurrentAnnotation({
        id,
        type: 'rectangle',
        x: pos.x,
        y: pos.y,
        width: 0,
        height: 0,
        color: activeColor,
        strokeWidth: activeThickness,
      });
    } else if (activeTool === 'circle') {
      setCurrentAnnotation({
        id,
        type: 'circle',
        x: pos.x,
        y: pos.y,
        radiusX: 0,
        radiusY: 0,
        color: activeColor,
        strokeWidth: activeThickness,
      });
    }
  };

  const handleMouseMove = (e: KonvaEventObject<MouseEvent>) => {
    if (!isDrawing || !currentAnnotation) return;
    const pos = getImageCoords(e);

    if (currentAnnotation.type === 'freehand') {
      setCurrentAnnotation({
        ...currentAnnotation,
        points: [...(currentAnnotation.points || []), pos.x, pos.y],
      });
    } else if (currentAnnotation.type === 'arrow') {
      const pts = currentAnnotation.points || [0, 0, 0, 0];
      setCurrentAnnotation({
        ...currentAnnotation,
        points: [pts[0], pts[1], pos.x, pos.y],
      });
    } else if (currentAnnotation.type === 'rectangle') {
      setCurrentAnnotation({
        ...currentAnnotation,
        width: pos.x - (currentAnnotation.x || 0),
        height: pos.y - (currentAnnotation.y || 0),
      });
    } else if (currentAnnotation.type === 'circle') {
      const cx = currentAnnotation.x || 0;
      const cy = currentAnnotation.y || 0;
      setCurrentAnnotation({
        ...currentAnnotation,
        radiusX: Math.abs(pos.x - cx),
        radiusY: Math.abs(pos.y - cy),
      });
    }
  };

  const handleMouseUp = () => {
    if (!isDrawing || !currentAnnotation) return;
    setIsDrawing(false);

    // Skip tiny annotations (accidental clicks)
    const isTiny =
      (currentAnnotation.type === 'freehand' && (currentAnnotation.points?.length || 0) < 6) ||
      (currentAnnotation.type === 'rectangle' && Math.abs(currentAnnotation.width || 0) < 3 && Math.abs(currentAnnotation.height || 0) < 3) ||
      (currentAnnotation.type === 'circle' && (currentAnnotation.radiusX || 0) < 3 && (currentAnnotation.radiusY || 0) < 3) ||
      (currentAnnotation.type === 'arrow' && currentAnnotation.points && Math.abs(currentAnnotation.points[2] - currentAnnotation.points[0]) < 3 && Math.abs(currentAnnotation.points[3] - currentAnnotation.points[1]) < 3);

    if (isTiny) {
      setCurrentAnnotation(null);
      return;
    }

    pushHistory([...annotations, currentAnnotation]);
    setCurrentAnnotation(null);
  };

  // ============================================
  // TEXT COMMIT
  // ============================================

  const commitText = () => {
    if (!textValue.trim()) {
      setTextInput({ ...textInput, visible: false });
      return;
    }

    const textPos = {
      x: (textInput.x - offset.x) / scale,
      y: (textInput.y - offset.y) / scale,
    };

    const ann: Annotation = {
      id: nextId(),
      type: 'text',
      x: textPos.x,
      y: textPos.y,
      text: textValue,
      color: activeColor,
      strokeWidth: activeThickness,
      fontSize: 18,
    };

    pushHistory([...annotations, ann]);
    setTextInput({ ...textInput, visible: false });
    setTextValue('');
  };

  // ============================================
  // EXPORT CANVAS TO BLOB
  // ============================================

  const getCanvasBlob = useCallback(async (): Promise<Blob> => {
    // Hide transformer before export
    if (transformerRef.current) {
      transformerRef.current.nodes([]);
      transformerRef.current.getLayer()?.batchDraw();
    }
    setSelectedId(null);

    // Wait a frame for re-render
    await new Promise((r) => setTimeout(r, 50));

    const stage = stageRef.current;
    if (!stage) throw new Error('Stage not available');

    const dataUrl = stage.toDataURL({ pixelRatio: 2, mimeType: 'image/png' });
    const resp = await fetch(dataUrl);
    return resp.blob();
  }, []);

  // ============================================
  // DOWNLOAD
  // ============================================

  const handleDownload = async () => {
    try {
      const b = await getCanvasBlob();
      const url = URL.createObjectURL(b);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${cameraName}_annotated_${new Date().toISOString().replace(/[:.]/g, '-')}.png`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  /**
   * Hand the (annotated) snapshot off to the global incident-creation
   * dialog, prefilling the camera's lat/lng so the new incident is
   * geo-located where the camera sits. Closes the editor on success.
   */
  const openIncidentDialogWithPrefill = useUIStore((s) => s.openIncidentDialogWithPrefill);
  const handleCreateIncident = async () => {
    try {
      const [annotatedBlob, device] = await Promise.all([
        getCanvasBlob(),
        cameraDeviceId
          ? devicesApi.get(cameraDeviceId).catch((err) => {
              console.warn('Device location lookup failed:', err);
              return null;
            })
          : Promise.resolve(null),
      ]);
      const ts = new Date().toISOString().replace(/[:.]/g, '-');
      const safeName = cameraName.replace(/[^A-Za-z0-9._-]/g, '_');
      const filename = `snapshot-${safeName}-${ts}.png`;
      const dataUrl = URL.createObjectURL(annotatedBlob);
      openIncidentDialogWithPrefill(
        {
          latitude: device?.latitude ?? undefined,
          longitude: device?.longitude ?? undefined,
          snapshotDataUrl: dataUrl,
        },
        { blob: annotatedBlob, filename },
      );
      onClose();
    } catch (err) {
      console.error('Create incident from snapshot failed:', err);
    }
  };

  // ============================================
  // KEYBOARD SHORTCUTS
  // ============================================

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (textInput.visible) return;
      if (e.ctrlKey && e.key === 'z') { e.preventDefault(); undo(); }
      if (e.ctrlKey && e.key === 'y') { e.preventDefault(); redo(); }
      if (e.key === 'Escape') onClose();
      if (e.key === 'Delete' && selectedId) {
        pushHistory(annotations.filter((a) => a.id !== selectedId));
        setSelectedId(null);
        if (transformerRef.current) {
          transformerRef.current.nodes([]);
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  // ============================================
  // FLOATING WINDOW DRAG & RESIZE
  // ============================================

  const handleWindowDragStart = (e: React.MouseEvent) => {
    // Only drag from the title bar area
    if ((e.target as HTMLElement).closest('button')) return;
    setIsDraggingWindow(true);
    dragOffset.current = { x: e.clientX - windowPos.x, y: e.clientY - windowPos.y };
    e.preventDefault();
  };

  const handleResizeStart = (e: React.MouseEvent) => {
    setIsResizing(true);
    resizeStart.current = { x: e.clientX, y: e.clientY, w: windowSize.width, h: windowSize.height };
    e.preventDefault();
    e.stopPropagation();
  };

  useEffect(() => {
    if (!isDraggingWindow && !isResizing) return;

    const handleMove = (e: MouseEvent) => {
      if (isDraggingWindow) {
        setWindowPos({
          x: Math.max(0, Math.min(window.innerWidth - 200, e.clientX - dragOffset.current.x)),
          y: Math.max(0, Math.min(window.innerHeight - 100, e.clientY - dragOffset.current.y)),
        });
      }
      if (isResizing) {
        setWindowSize({
          width: Math.max(500, resizeStart.current.w + (e.clientX - resizeStart.current.x)),
          height: Math.max(400, resizeStart.current.h + (e.clientY - resizeStart.current.y)),
        });
      }
    };

    const handleUp = () => {
      setIsDraggingWindow(false);
      setIsResizing(false);
    };

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [isDraggingWindow, isResizing]);

  // ============================================
  // RENDER ANNOTATION
  // ============================================

  const renderAnnotation = (ann: Annotation, isPreview = false) => {
    const opacity = isPreview ? 0.7 : 1;
    const common = { id: ann.id, key: ann.id, opacity, draggable: activeTool === 'select' && !isPreview };

    switch (ann.type) {
      case 'freehand':
        return (
          <Line
            {...common}
            points={ann.points || []}
            stroke={ann.color}
            strokeWidth={ann.strokeWidth}
            lineCap="round"
            lineJoin="round"
            tension={0.5}
          />
        );
      case 'arrow':
        return (
          <Arrow
            {...common}
            points={ann.points || []}
            stroke={ann.color}
            strokeWidth={ann.strokeWidth}
            fill={ann.color}
            pointerLength={10}
            pointerWidth={8}
          />
        );
      case 'rectangle':
        return (
          <Rect
            {...common}
            x={ann.x || 0}
            y={ann.y || 0}
            width={ann.width || 0}
            height={ann.height || 0}
            stroke={ann.color}
            strokeWidth={ann.strokeWidth}
          />
        );
      case 'circle':
        return (
          <Circle
            {...common}
            x={ann.x || 0}
            y={ann.y || 0}
            radiusX={ann.radiusX || 0}
            radiusY={ann.radiusY || 0}
            stroke={ann.color}
            strokeWidth={ann.strokeWidth}
          />
        );
      case 'text':
        return (
          <KonvaText
            {...common}
            x={ann.x || 0}
            y={ann.y || 0}
            text={ann.text || ''}
            fill={ann.color}
            fontSize={ann.fontSize || 18}
            fontFamily="Inter, sans-serif"
            fontStyle="bold"
          />
        );
      default:
        return null;
    }
  };

  // ============================================
  // CURSOR
  // ============================================

  const getCursor = () => {
    switch (activeTool) {
      case 'select': return 'default';
      case 'eraser': return 'pointer';
      case 'text': return 'text';
      default: return 'crosshair';
    }
  };

  // ============================================
  // RENDER
  // ============================================

  return (
    <>
      {/* Floating window - no overlay, no blocking */}
      <div
        ref={windowRef}
        className="fixed bg-gray-900 border border-gray-600 rounded-lg z-[60] flex flex-col overflow-hidden shadow-2xl"
        style={{
          left: windowPos.x,
          top: windowPos.y,
          width: windowSize.width,
          height: windowSize.height,
        }}
      >
        {/* ============ TITLE BAR (draggable) ============ */}
        <div
          className="flex items-center gap-2 px-3 py-1.5 bg-gray-800 border-b border-gray-700 cursor-move select-none flex-shrink-0"
          onMouseDown={handleWindowDragStart}
        >
          <Camera className="w-4 h-4 text-primary-400" />
          <span className="text-xs font-medium text-gray-300 flex-1 truncate">{t('snapshotEditor')} {cameraName}</span>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-gray-700 text-gray-400 hover:text-white" title={t('closeEsc')}>
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

          {/* ============ TOOLBAR ============ */}
          <div className="flex items-center gap-1 px-3 py-2 bg-gray-800/50 border-b border-gray-700 flex-shrink-0 flex-wrap">
            {/* Drawing tools */}
            <div className="flex items-center gap-0.5 me-2">
              {TOOLS.map(({ tool, icon: Icon, label }) => (
                <button
                  key={tool}
                  type="button"
                  onClick={() => { setActiveTool(tool); setSelectedId(null); if (transformerRef.current) transformerRef.current.nodes([]); }}
                  className={`p-2 rounded transition-colors ${activeTool === tool ? 'bg-primary-600 text-white' : 'text-gray-400 hover:bg-gray-700 hover:text-white'}`}
                  title={label}
                >
                  <Icon className="w-4 h-4" />
                </button>
              ))}
            </div>

            {/* Separator */}
            <div className="w-px h-6 bg-gray-600 mx-1" />

            {/* Color picker */}
            <div className="flex items-center gap-1 me-2">
              {COLORS.map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setActiveColor(value)}
                  className={`w-5 h-5 rounded-full border-2 transition-all ${activeColor === value ? 'border-white scale-125' : 'border-gray-600 hover:border-gray-400'}`}
                  style={{ backgroundColor: value }}
                  title={label}
                />
              ))}
            </div>

            {/* Separator */}
            <div className="w-px h-6 bg-gray-600 mx-1" />

            {/* Line thickness */}
            <div className="flex items-center gap-1 me-2">
              {THICKNESSES.map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setActiveThickness(value)}
                  className={`px-2 py-1 rounded text-xs transition-colors ${activeThickness === value ? 'bg-primary-600 text-white' : 'text-gray-400 hover:bg-gray-700 hover:text-white'}`}
                  title={label}
                >
                  <div className="w-5 flex items-center justify-center">
                    <div className="rounded-full bg-current" style={{ width: '100%', height: value }} />
                  </div>
                </button>
              ))}
            </div>

            {/* Separator */}
            <div className="w-px h-6 bg-gray-600 mx-1" />

            {/* Undo / Redo */}
            <button type="button" onClick={undo} disabled={historyIndex <= 0} className="p-2 rounded text-gray-400 hover:bg-gray-700 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed" title={t('undoCtrlZ')}>
              <Undo2 className="w-4 h-4" />
            </button>
            <button type="button" onClick={redo} disabled={historyIndex >= history.length - 1} className="p-2 rounded text-gray-400 hover:bg-gray-700 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed" title={t('redoCtrlY')}>
              <Redo2 className="w-4 h-4" />
            </button>

            {/* Spacer */}
            <div className="flex-1" />

            {/* Action buttons */}
            <button type="button" onClick={handleDownload} className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-gray-700 text-gray-200 hover:bg-gray-600 text-sm transition-colors" title={t('downloadAnnotatedSnapshot')}>
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">{t('download')}</span>
            </button>
            <button type="button" onClick={() => setShowChatPicker(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-500 text-sm transition-colors" title={t('sendViaChat')}>
              <MessageSquare className="w-4 h-4" />
              <span className="hidden sm:inline">{t('chat')}</span>
            </button>
            <button type="button" onClick={() => setShowEmailPicker(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-green-600 text-white hover:bg-green-500 text-sm transition-colors" title={t('sendViaEmail')}>
              <Mail className="w-4 h-4" />
              <span className="hidden sm:inline">{t('email')}</span>
            </button>
            <button type="button" onClick={handleCreateIncident} className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-orange-600 text-white hover:bg-orange-500 text-sm transition-colors" title={t('createIncidentWithThisSnapshot')}>
              <AlertTriangle className="w-4 h-4" />
              <span className="hidden sm:inline">{t('createIncident')}</span>
            </button>

          </div>

          {/* ============ CANVAS ============ */}
          <div ref={containerRef} className="flex-1 relative overflow-hidden bg-gray-950" style={{ cursor: getCursor() }}>
            {image && (
              <Stage
                ref={stageRef as any}
                width={stageSize.width}
                height={stageSize.height}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onTouchStart={handleMouseDown as any}
                onTouchMove={handleMouseMove as any}
                onTouchEnd={handleMouseUp}
              >
                {/* Background image layer (+ baked-in brand watermark) */}
                <Layer listening={false}>
                  <KonvaImage
                    image={image}
                    x={offset.x}
                    y={offset.y}
                    width={imageSize.width * scale}
                    height={imageSize.height * scale}
                  />
                  {watermark && (() => {
                    // IOSentra brand watermark: sized to ~10% of the image width,
                    // min 52px, placed bottom-right with a small margin. Opacity
                    // kept high enough to be clearly legible on a download but low
                    // enough not to obscure evidence in the frame.
                    const wmSize = Math.max(52, imageSize.width * scale * 0.10);
                    const margin = wmSize * 0.32;
                    return (
                      <KonvaImage
                        image={watermark}
                        x={offset.x + imageSize.width * scale - wmSize - margin}
                        y={offset.y + imageSize.height * scale - wmSize - margin}
                        width={wmSize}
                        height={wmSize}
                        opacity={0.82}
                      />
                    );
                  })()}
                </Layer>

                {/* Annotations layer */}
                <Layer x={offset.x} y={offset.y} scaleX={scale} scaleY={scale}>
                  {annotations.map((ann) => renderAnnotation(ann))}
                  {currentAnnotation && renderAnnotation(currentAnnotation, true)}
                  <Transformer ref={transformerRef as any} rotateEnabled={false} borderStroke="#3b82f6" anchorFill="#3b82f6" anchorStroke="#1e40af" />
                </Layer>
              </Stage>
            )}

            {/* Text input overlay */}
            {textInput.visible && (
              <input
                ref={textInputRef}
                type="text"
                value={textValue}
                onChange={(e) => setTextValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') commitText(); if (e.key === 'Escape') setTextInput({ ...textInput, visible: false }); }}
                onBlur={commitText}
                className="absolute bg-transparent border-2 border-dashed border-white text-white px-1 py-0.5 text-lg font-bold outline-none"
                style={{
                  left: textInput.x,
                  top: textInput.y,
                  color: activeColor,
                  minWidth: 100,
                  zIndex: 10,
                }}
                placeholder={t('typeText')}
              />
            )}

            {/* Loading state */}
            {!image && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
              </div>
            )}
          </div>

          {/* ============ SUB-DIALOGS ============ */}
          {showChatPicker && (
            <ChatRecipientPicker
              getBlob={getCanvasBlob}
              cameraName={cameraName}
              onClose={() => setShowChatPicker(false)}
            />
          )}
          {showEmailPicker && (
            <EmailRecipientPicker
              getBlob={getCanvasBlob}
              cameraName={cameraName}
              onClose={() => setShowEmailPicker(false)}
            />
          )}
        {/* Resize handle */}
        <div
          className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize z-10"
          onMouseDown={handleResizeStart}
          style={{ background: 'linear-gradient(135deg, transparent 50%, rgba(156,163,175,0.5) 50%)' }}
        />
      </div>
    </>
  );
}

// ============================================
// CHAT RECIPIENT PICKER
// ============================================

interface RecipientPickerProps {
  getBlob: () => Promise<Blob>;
  cameraName: string;
  onClose: () => void;
}

function ChatRecipientPicker({ getBlob, cameraName, onClose }: RecipientPickerProps) {
  const { t } = useTranslation('cameras');
  const [users, setUsers] = useState<Array<{ id: string; firstName: string; lastName: string; email: string }>>([]);
  const [groups, setGroups] = useState<Array<{ id: string; name: string }>>([]);
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [selectedGroups, setSelectedGroups] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const chatStore = useChatStore();

  useEffect(() => {
    usersApi.list().then((data) => {
      const list = Array.isArray(data) ? data : (data as any)?.items || [];
      setUsers(list.map((u: any) => ({ id: u.id, firstName: u.firstName || '', lastName: u.lastName || '', email: u.email || '' })));
    }).catch(() => {});

    userGroupsApi.list().then((data) => {
      const list = Array.isArray(data) ? data : (data as any)?.items || [];
      setGroups(list.map((g: any) => ({ id: g.id, name: g.name || '' })));
    }).catch(() => {});
  }, []);

  const toggleUser = (id: string) => {
    setSelectedUsers((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleGroup = (id: string) => {
    setSelectedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const filteredUsers = users.filter((u) => {
    const q = search.toLowerCase();
    return u.firstName.toLowerCase().includes(q) || u.lastName.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
  });

  const filteredGroups = groups.filter((g) => g.name.toLowerCase().includes(search.toLowerCase()));

  const handleSend = async () => {
    if (selectedUsers.size === 0 && selectedGroups.size === 0) return;
    setSending(true);
    setError(null);

    try {
      const imageBlob = await getBlob();
      const file = new File([imageBlob], `snapshot-${cameraName}-${Date.now()}.png`, { type: 'image/png' });

      // Helper: send snapshot to a conversation
      const sendToConversation = async (convId: string) => {
        // 1. Send message first to get messageId
        const message = await chatApi.sendMessage(convId, {
          content: `📷 Snapshot from camera: ${cameraName}`,
          messageType: 'image',
        });

        // 2. Upload file with conversationId + messageId
        const { getAuthHeaders } = useAuthStore.getState();
        const formData = new FormData();
        formData.append('file', file);
        formData.append('conversationId', convId);
        formData.append('messageId', message.id);
        await fetch(`${getApiBase()}/chat/upload`, {
          method: 'POST',
          headers: { ...getAuthHeaders() },
          body: formData,
        });
      };

      // Send to individual users
      for (const userId of selectedUsers) {
        try {
          const conv = await chatApi.createDirectConversation(userId);
          await sendToConversation(conv.id);
        } catch (err) {
          console.error(`Failed to send to user ${userId}:`, err);
        }
      }

      // Send to groups
      for (const groupId of selectedGroups) {
        try {
          const conv = await chatApi.createFromUserGroup(groupId);
          await sendToConversation(conv.id);
        } catch (err) {
          console.error(`Failed to send to group ${groupId}:`, err);
        }
      }

      setSent(true);
      setTimeout(onClose, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send snapshot');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="absolute inset-0 z-[70] flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md bg-gray-800 rounded-lg shadow-xl border border-gray-600 flex flex-col max-h-[70vh]">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
          <h3 className="text-sm font-semibold text-white flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-blue-400" />
            {t('sendSnapshotViaChat')}
          </h3>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-gray-700 text-gray-400"><X className="w-4 h-4" /></button>
        </div>

        {/* Search */}
        <div className="px-4 py-2 border-b border-gray-700">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full ps-8 pe-3 py-1.5 bg-gray-700 border border-gray-600 rounded text-sm text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500"
              placeholder={t('searchUsersOrGroups')}
              autoFocus
            />
          </div>
        </div>

        {/* Recipients list */}
        <div className="flex-1 overflow-y-auto">
          {/* Groups */}
          {filteredGroups.length > 0 && (
            <div>
              <div className="px-4 py-1.5 text-xs font-medium text-gray-500 uppercase tracking-wider">{t('groups')}</div>
              {filteredGroups.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => toggleGroup(g.id)}
                  className={`w-full flex items-center gap-3 px-4 py-2 text-start hover:bg-gray-700 transition-colors ${selectedGroups.has(g.id) ? 'bg-gray-700/50' : ''}`}
                >
                  <div className="w-7 h-7 rounded-full bg-purple-600 flex items-center justify-center flex-shrink-0">
                    <Users className="w-3.5 h-3.5 text-white" />
                  </div>
                  <span className="text-sm text-white flex-1 truncate">{g.name}</span>
                  {selectedGroups.has(g.id) && <Check className="w-4 h-4 text-primary-400" />}
                </button>
              ))}
            </div>
          )}

          {/* Users */}
          {filteredUsers.length > 0 && (
            <div>
              <div className="px-4 py-1.5 text-xs font-medium text-gray-500 uppercase tracking-wider">{t('users')}</div>
              {filteredUsers.map((u) => (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => toggleUser(u.id)}
                  className={`w-full flex items-center gap-3 px-4 py-2 text-start hover:bg-gray-700 transition-colors ${selectedUsers.has(u.id) ? 'bg-gray-700/50' : ''}`}
                >
                  <div className="w-7 h-7 rounded-full bg-primary-600 flex items-center justify-center text-white text-xs font-medium flex-shrink-0">
                    {u.firstName[0] || '?'}{u.lastName[0] || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">{u.firstName} {u.lastName}</p>
                    <p className="text-xs text-gray-400 truncate">{u.email}</p>
                  </div>
                  {selectedUsers.has(u.id) && <Check className="w-4 h-4 text-primary-400" />}
                </button>
              ))}
            </div>
          )}

          {filteredUsers.length === 0 && filteredGroups.length === 0 && (
            <div className="p-8 text-center text-gray-400 text-sm">{t('noUsersOrGroupsFound')}</div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-700 flex items-center justify-between">
          <span className="text-xs text-gray-400">
            {selectedUsers.size + selectedGroups.size} {t('selected')}
          </span>
          {error && <span className="text-xs text-red-400">{error}</span>}
          {sent && <span className="text-xs text-green-400">{t('sentSuccessfully')}</span>}
          <button
            type="button"
            onClick={handleSend}
            disabled={sending || sent || (selectedUsers.size === 0 && selectedGroups.size === 0)}
            className="flex items-center gap-1.5 px-4 py-1.5 rounded bg-blue-600 text-white text-sm hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {sending ? 'Sending...' : sent ? 'Sent!' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// EMAIL RECIPIENT PICKER
// ============================================

function EmailRecipientPicker({ getBlob, cameraName, onClose }: RecipientPickerProps) {
  const { t } = useTranslation('cameras');
  const [users, setUsers] = useState<Array<{ id: string; firstName: string; lastName: string; email: string }>>([]);
  const [selectedEmails, setSelectedEmails] = useState<Set<string>>(new Set());
  const [manualEmail, setManualEmail] = useState('');
  const [search, setSearch] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    usersApi.list().then((data) => {
      const list = Array.isArray(data) ? data : (data as any)?.items || [];
      setUsers(list.map((u: any) => ({ id: u.id, firstName: u.firstName || '', lastName: u.lastName || '', email: u.email || '' })));
    }).catch(() => {});
  }, []);

  const toggleEmail = (email: string) => {
    setSelectedEmails((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email); else next.add(email);
      return next;
    });
  };

  const addManualEmail = () => {
    const email = manualEmail.trim();
    if (email && email.includes('@')) {
      setSelectedEmails((prev) => new Set(prev).add(email));
      setManualEmail('');
    }
  };

  const filteredUsers = users.filter((u) => {
    if (!u.email) return false;
    const q = search.toLowerCase();
    return u.firstName.toLowerCase().includes(q) || u.lastName.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
  });

  const handleSend = async () => {
    if (selectedEmails.size === 0) return;
    setSending(true);
    setError(null);

    try {
      const imageBlob = await getBlob();
      const { getAuthHeaders } = useAuthStore.getState();
      const formData = new FormData();
      formData.append('snapshot', imageBlob, `snapshot-${cameraName}-${Date.now()}.png`);
      formData.append('recipients', JSON.stringify(Array.from(selectedEmails)));
      formData.append('cameraName', cameraName);

      const response = await fetch(`${getApiBase()}/snapshots/send-email`, {
        method: 'POST',
        headers: { ...getAuthHeaders() },
        body: formData,
      });

      if (!response.ok) throw new Error('Failed to send email');

      setSent(true);
      setTimeout(onClose, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="absolute inset-0 z-[70] flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md bg-gray-800 rounded-lg shadow-xl border border-gray-600 flex flex-col max-h-[70vh]">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
          <h3 className="text-sm font-semibold text-white flex items-center gap-2">
            <Mail className="w-4 h-4 text-green-400" />
            {t('sendSnapshotViaEmail')}
          </h3>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-gray-700 text-gray-400"><X className="w-4 h-4" /></button>
        </div>

        {/* Manual email input */}
        <div className="px-4 py-2 border-b border-gray-700">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Mail className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="email"
                value={manualEmail}
                onChange={(e) => setManualEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addManualEmail(); } }}
                className="w-full ps-8 pe-3 py-1.5 bg-gray-700 border border-gray-600 rounded text-sm text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500"
                placeholder={t('enterEmailAddress')}
              />
            </div>
            <button type="button" onClick={addManualEmail} className="px-3 py-1.5 bg-gray-700 border border-gray-600 rounded text-sm text-gray-300 hover:bg-gray-600">{t('add')}</button>
          </div>
          {/* Selected emails tags */}
          {selectedEmails.size > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {Array.from(selectedEmails).map((email) => (
                <span key={email} className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-900/40 border border-green-700/50 rounded-full text-xs text-green-300">
                  {email}
                  <button type="button" onClick={() => toggleEmail(email)} className="hover:text-white"><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Search */}
        <div className="px-4 py-2 border-b border-gray-700">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full ps-8 pe-3 py-1.5 bg-gray-700 border border-gray-600 rounded text-sm text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500"
              placeholder={t('searchUsers')}
            />
          </div>
        </div>

        {/* Users list */}
        <div className="flex-1 overflow-y-auto">
          {filteredUsers.map((u) => (
            <button
              key={u.id}
              type="button"
              onClick={() => toggleEmail(u.email)}
              className={`w-full flex items-center gap-3 px-4 py-2 text-start hover:bg-gray-700 transition-colors ${selectedEmails.has(u.email) ? 'bg-gray-700/50' : ''}`}
            >
              <div className="w-7 h-7 rounded-full bg-primary-600 flex items-center justify-center text-white text-xs font-medium flex-shrink-0">
                {u.firstName[0] || '?'}{u.lastName[0] || '?'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white truncate">{u.firstName} {u.lastName}</p>
                <p className="text-xs text-gray-400 truncate">{u.email}</p>
              </div>
              {selectedEmails.has(u.email) && <Check className="w-4 h-4 text-green-400" />}
            </button>
          ))}
          {filteredUsers.length === 0 && (
            <div className="p-8 text-center text-gray-400 text-sm">{t('noUsersFound')}</div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-700 flex items-center justify-between">
          <span className="text-xs text-gray-400">{selectedEmails.size} {t('recipientS')}</span>
          {error && <span className="text-xs text-red-400">{error}</span>}
          {sent && <span className="text-xs text-green-400">{t('sentSuccessfully')}</span>}
          <button
            type="button"
            onClick={handleSend}
            disabled={sending || sent || selectedEmails.size === 0}
            className="flex items-center gap-1.5 px-4 py-1.5 rounded bg-green-600 text-white text-sm hover:bg-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {sending ? 'Sending...' : sent ? 'Sent!' : 'Send Email'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Helper to get API base URL (matches pattern from api.ts)
function getApiBase(): string {
  return import.meta.env.VITE_API_URL || '/api/v1';
}
